'use client';

import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    FormControl,
    FormControlLabel,
    FormLabel,
    Paper,
    Radio,
    RadioGroup,
    Typography,
    Alert,
    LinearProgress,
    Link,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from '@mui/material';
import {
    CheckCircle,
    Quiz as QuizIcon,
    ErrorOutline,
    ExpandLess,
    ExpandMore,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles'; // Import useTheme
import ReturnBack from '../../components/reusable/accordions/ReturnBack';
import SEO from '../../components/reusable/SEO';
import { ExternalLink } from '../../utils/SiteHelpers';
import { config } from '../../config/config';
import { useCourses } from '../../context/CourseContext';

const legalQuizQuestions = [
    {
        id: 'q1',
        question:
            'Do you acknowledge that it is your responsibility to self report this continuing legal education webinar to your bar organization?',
        options: [
            { value: 'yes', label: 'Yes', correct: true },
            { value: 'no', label: 'No', correct: false },
        ],
    },
    {
        id: 'q2',
        question: (
            <Box>
                Do you acknowledge that it is your responsibility to review our
                approved{' '}
                <Link
                    href={`${config.siteUrl}/webinars/jurisdictions`}
                    target="_blank"
                    rel="noopener"
                    sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                            color: 'primary.main', // keep it the same
                        },
                    }}
                >
                    Jurisdictions
                </Link>{' '}
                resource and confirm with your own bar organization whether or
                not they will accept your certificate of completion for this
                webinar?
            </Box>
        ),
        options: [
            { value: 'yes', label: 'Yes', correct: true },
            { value: 'no', label: 'No', correct: false },
        ],
    },
    {
        id: 'q3',
        question:
            'Do you acknowledge that you must watch 100% of this entire webinar in order to earning any and all credit for this webinar, since we do not give partial credit for any of our webinars?',
        options: [
            { value: 'yes', label: 'Yes', correct: true },
            { value: 'no', label: 'No', correct: false },
        ],
    },
    {
        id: 'q4',
        question: (
            <Box>
                Do you acknowledge that we offer a{' '}
                <Link
                    href={`${config.siteUrl}/webinars/share-cle`}
                    target="_blank"
                    rel="noopener"
                    sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                            color: 'primary.main', // keep it the same
                        },
                    }}
                >
                    Share CLE
                </Link>{' '}
                resource in our Webinars section so you can help us share
                Toolkit.law and our CLE with your colleagues and bar
                organization?{' '}
            </Box>
        ),
        options: [
            { value: 'yes', label: 'Yes', correct: true },
            { value: 'no', label: 'No', correct: false },
        ],
    },
    {
        id: 'q5',
        question:
            'Do you acknowledge that you alone must personally complete 100% of any of our courses in order to submit any completed courses for credit?',
        options: [
            { value: 'yes', label: 'Yes', correct: true },
            { value: 'no', label: 'No', correct: false },
        ],
    },
];

function Quiz() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { courseId } = useParams();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState('');
    const [error, setError] = useState('');
    const [quizCompleted, setQuizCompleted] = useState(false);
    const [answeredQuestions, setAnsweredQuestions] = useState([]);
    const currentQuestion = legalQuizQuestions[currentQuestionIndex];
    const progress =
        (answeredQuestions.length / legalQuizQuestions.length) * 100;

    const { courses, updateCourseProgress, getCourseTopics, topics } =
        useCourses();
    const [course, setCourse] = useState(null);
    const [alreadyCompleted, setAlreadyCompleted] = useState(false);
    const [showQuizDetails, setShowQuizDetails] = useState(false);

    useEffect(() => {
        if (courses.length > 0 && courseId) {
            const selectedCourse = courses.find((c) => c.slug === courseId);
            setCourse(selectedCourse);
        }
    }, [courses, courseId]);

    const handleAnswerChange = (event) => {
        setSelectedAnswer(event.target.value);
        setError('');
    };

    const handleNextQuestion = () => {
        const selectedOption = currentQuestion.options.find(
            (option) => option.value === selectedAnswer,
        );

        if (!selectedOption) {
            setError('Please select an answer before proceeding.');
            return;
        }

        if (!selectedOption.correct) {
            setError('Please select the correct answer to proceed.');
            return;
        }

        // Add current question to answered questions
        setAnsweredQuestions([...answeredQuestions, currentQuestion.id]);

        // Move to next question or complete quiz
        if (currentQuestionIndex < legalQuizQuestions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
            setSelectedAnswer('');
            setError('');
        } else {
            setQuizCompleted(true);
            // Save quiz completion status
            if (course) {
                updateCourseProgress(course.id, 3, 'quiz'); // use real course.id
                getCourseTopics(course.id);
            }
        }
    };

    const handleContinueToCourse = async () => {
        const firstLessonId = topics?.[0]?.id;
        navigate(`/webinars/lms/${courseId}/${firstLessonId}`);
    };

    const handleBacktoWebinars = () => {
        navigate(`/webinars/lms`);
    };

    useEffect(() => {
        if (topics.length > 0) {
            const lesson = topics.find((c) => c.id === 3);
            console.log('lesson', lesson);
            // Check if quiz is already completed
            if (lesson?.is_completed) {
                setAlreadyCompleted(true);
                setQuizCompleted(true);
                // Mark all questions as answered for the summary
                setAnsweredQuestions(legalQuizQuestions.map((q) => q.id));
            }
        }
    }, [topics]);
    const toggleQuizDetails = () => {
        setShowQuizDetails(!showQuizDetails);
    };
    const renderQuizSummary = () => (
        <Box sx={{ my: 1 }}>
            <Button
                variant="outlined"
                onClick={toggleQuizDetails}
                endIcon={showQuizDetails ? <ExpandLess /> : <ExpandMore />}
                sx={{
                    color: 'black',
                    border: 'none !important',
                    borderBottom: '1px solid lightgray !important',
                    borderRadius: '0px !important',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between;',
                }}
            >
                Quiz Details
            </Button>

            {showQuizDetails && (
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Question</TableCell>
                                <TableCell>Your Answer</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {legalQuizQuestions.map((question, index) => (
                                <TableRow key={question.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                        {typeof question.question === 'string'
                                            ? question.question
                                            : question.question.props.children}
                                    </TableCell>
                                    <TableCell>Yes</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Box sx={{ textAlign: 'center', mt: 3 }}>
                <CheckCircle
                    sx={{
                        fontSize: 60,
                        mb: 2,
                        color: '#f7971c',
                    }}
                />
                <Typography variant="h5" gutterBottom>
                    Quiz Score: 100%
                </Typography>

                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleContinueToCourse}
                    sx={{ px: 4, py: 1.5, mt: 2 }}
                >
                    Next
                </Button>
            </Box>
        </Box>
    );
    return (
        <Box>
            <SEO pageTitle="Quiz | Toolkit.law" />

            <ReturnBack pageTitle="Quiz" />

            <Container maxWidth="md" sx={{ py: 4 }}>
                <Box sx={{ p: 3 }} />

                <Card sx={{ mb: 4, boxShadow: 4 }}>
                    <CardHeader
                        sx={{
                            backgroundColor: theme.palette.primary.light,
                            py: 2,
                        }}
                        title={
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <QuizIcon sx={{ mr: 1 }} />
                                    <Typography variant="h6">
                                        Legal Education Quiz
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    {answeredQuestions.length}/
                                    {legalQuizQuestions.length} Questions
                                    Answered
                                </Typography>
                            </Box>
                        }
                        disableTypography
                    />

                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ height: 8 }}
                        color="inherit"
                    />

                    <CardContent sx={{ p: 4 }}>
                        {!quizCompleted ? (
                            <>
                                <Typography variant="body1" paragraph>
                                    You must select the correct answer to all
                                    questions to unlock the webinar content.
                                </Typography>

                                {/* Current Question */}
                                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                                    <FormControl
                                        component="fieldset"
                                        sx={{ width: '100%' }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{ fontWeight: 'bold', mb: 1 }}
                                        >
                                            Question {currentQuestionIndex + 1}:{' '}
                                            {currentQuestion.question}
                                        </Typography>
                                        <RadioGroup
                                            value={selectedAnswer}
                                            onChange={handleAnswerChange}
                                        >
                                            {currentQuestion.options.map(
                                                (option) => (
                                                    <FormControlLabel
                                                        key={option.value}
                                                        value={option.value}
                                                        control={<Radio />}
                                                        label={option.label}
                                                    />
                                                ),
                                            )}
                                        </RadioGroup>
                                    </FormControl>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        {' '}
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleBacktoWebinars}
                                        >
                                            Back to Webinars
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleNextQuestion}
                                            disabled={!selectedAnswer}
                                        >
                                            Submit
                                        </Button>
                                    </Box>
                                </Paper>

                                {error && (
                                    <Alert
                                        severity="error"
                                        icon={<ErrorOutline />}
                                        sx={{ mb: 3 }}
                                    >
                                        {error}
                                    </Alert>
                                )}
                            </>
                        ) : (
                            renderQuizSummary()
                        )}
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
}

export default Quiz;
